import axios from "axios";
import { isObject } from "lodash";
import { createChunkedAxiosAdapter, DEFAULT_TRACKED_ERRORS } from "pixel-request-chunks";
import { getBaseUrl, getIsUserLoggedIn, getRefreshToken, getUserToken } from "../helpers";
import Notify from "@src/@core/components/Notify/ActionNotify";

import { convertToFormData, measureFormData } from "./formData";
import { applyRequestEncryption, decryptResponseProps } from "./encryption";
import { handleRateLimit } from "./rateLimit";
import { initSubmissionToast, showProgressToast, updateProgressToast, hideProgressToast } from "./toast";
import { attachAuthToken, buildRequestParams } from "./requestHelpers";
import { refreshAuthAndRetry } from "./refreshAuth";
import { logoutIfSessionDead } from "../sessionGuard";
import { flagMaintenanceIfDown } from "../maintenance";

let abortController = new AbortController();

class CrudApiService {
  apiInstance = undefined;

  create = (config) => {
    this.init(config);
    return this.apiInstance;
  };

  init = (config) => {
    const MB = 1024 * 1024; // 1 megabyte in bytes

    this.config = {
      baseURL: getBaseUrl(), // base API URL for all requests
      useFormData: true, // auto-convert request payload into FormData
      withAdditionalParams: false, // merge global params (from local storage) into the request
      disableProgress: false, // disable the upload progress toast
      progressThresholdMB: 0.2, // only show the upload progress toast when payload exceeds this size (MB) or carries files
      keys: [], // payload field names that should be encrypted before sending
      useFilteredBranches: true, // append filtered_branches_ids from local storage to params
      maxRateLimit: 20, // max number of requests allowed per minute per URL
      maxRateLimitMessage: "Requests have been temporarily blocked for 1 minute. Please try again later.", // message shown when rate limit is hit
      chunkThresholdMB: 0.5, // write all requests as chunked when payload size exceeds this threshold (MB) — under it one plain POST is cheaper than start + chunk + complete
      chunkSizeMB: 1, // size of each uploaded chunk (MB) — matches the backend's chunk_size config
      maxFormDataEntries: 1000, // max FormData fields before forcing chunking
      saveSnapshot: true, // persist failed chunked request as snapshot for later retry
      snapshotTime: 12, // keep resumable snapshots for 12 hours
      trackErrors: DEFAULT_TRACKED_ERRORS, // which errors are worth tracking for retry (skips 4xx, abort, etc.)
      ...config
    };

    this.instance = axios.create({
      ...this.config,
      adapter: createChunkedAxiosAdapter(axios, {
        chunkThresholdBytes: Math.round(this.config.chunkThresholdMB * MB), // convert MB → bytes for the adapter
        chunkSize: Math.round(this.config.chunkSizeMB * MB), // convert MB → bytes for the adapter
        maxFormDataEntries: this.config.maxFormDataEntries,
        useFormData: this.config.useFormData,
        saveSnapshot: !!this.config.saveSnapshot,
        snapshotTTL: (this.config.snapshotTime * 60 * 60 * 1000) || 0, // convert hours → milliseconds for the adapter
        trackErrors: this.config.trackErrors
      })
    });

    this.toastId = initSubmissionToast(
      this.config.toastTitle || "User Submission",
      this.abortXhrRequest
    );

    this.applyTransformers();
    this.applyInterceptors();
    this.apiInstance = this.instance;

    return this;
  };
  applyTransformers = () => {
    this.instance.defaults.transformResponse = [
      ...axios.defaults.transformResponse,
      (data, headers) => {
        if (headers["content-type"] === "application/json") {
          if (typeof data === "string" && data.trim() !== "") {
            try {
              return JSON.parse(data);
            } catch (e) {
              return data;
            }
          }
        }
        return data;
      }
    ];
  };

  applyInterceptors = () => {
    this.instance.interceptors.request.use(this.onRequest, this.onRequestError);
    this.instance.interceptors.response.use(this.onResponse, this.onResponseError);
  };

  setToastTitle = (title) => {
    this.config.toastTitle = title;
    return this;
  };

  setEncryptedKeys = (keys = []) => {
    this.config.keys = keys;
    return this;
  };

  setUseFormData = (status = false) => {
    this.config.useFormData = status;
    return this;
  };

  setWithAdditionalParams = (status = false) => {
    this.config.withAdditionalParams = status;
    return this;
  };

  setDisableProgressBar = (status = false) => {
    this.config.disableProgress = status;
    return this;
  };

  abortXhrRequest = () => {
    if (confirm("Are you sure you want to cancel the form submission request?")) {
      window.onbeforeunload = null;
      abortController.abort("Request canceled by the user.");
      hideProgressToast(this.toastId);
      setTimeout(() => {
        abortController = new AbortController();
      }, 500);
    }
  };

  handleFormData = () => {
    if (this.config.useFormData == true && isObject(this.config?.data)) {
      this.config.data = convertToFormData(this.config.data);

      const { hasFiles, bytes } = measureFormData(this.config.data);
      const thresholdBytes = (this.config.progressThresholdMB || 0) * 1024 * 1024;

      if (this.config.disableProgress == false && (hasFiles || bytes > thresholdBytes)) {
        showProgressToast(this.toastId);
        this.config.onUploadProgress = (e) => updateProgressToast(this.toastId, e);
        this.config.signal = abortController.signal;
      }
    }
    return this;
  };

  onRequest = async (config) => {
    const limitExceeded = handleRateLimit(config);

    if (limitExceeded) {
      Notify.Error({
        title: "Rate Limit Exceeded - Too Many Requests",
        message: config.maxRateLimitMessage
      });
      const err = new Error("Rate limit exceeded");
      err.response = {
        status: 429,
        statusText: "Too Many Requests",
        data: { message: config.maxRateLimitMessage },
        headers: {},
        config
      };
      err.config = config;
      return Promise.reject(err);
    }

    this.config = { ...config, ...this.config };

    if (this.config.method?.toLowerCase() === "delete") { //according to backend
      this.config.useChunk = false;
      this.config.useFormData = false;
    }

    buildRequestParams(this.config);
    attachAuthToken(this.config);
    applyRequestEncryption(this.config);
    this.handleFormData();

    return this.config;
  };

  onRequestError = (error) => Promise.reject(error);

  onResponse = (response) => {
    hideProgressToast(this.toastId);
    return decryptResponseProps(response);
  };

  onResponseError = (error) => {
    hideProgressToast(this.toastId);

    const originalRequest = error?.config;
    const isRefreshRequest = (originalRequest?.url || "").includes("/api/auth/refresh-token");
    const canRefresh = getIsUserLoggedIn() && Boolean(getUserToken()) && Boolean(getRefreshToken());

    if (error?.response?.status === 401 && canRefresh && originalRequest && !originalRequest._isRetry && !isRefreshRequest) {
      originalRequest._isRetry = true;
      return refreshAuthAndRetry(error);
    }

    logoutIfSessionDead(error);
    flagMaintenanceIfDown(error);

    return Promise.reject(error);
  };
}

const CrudApi = (config = {}) => {
  return new CrudApiService().create(config);
};

export default CrudApi;
